// Watches the host page's URL so the widget can re-evaluate URL-targeted
// surveys as the user navigates. Hard navigations re-bootstrap the snippet,
// so only SPA-style changes need interception: `history.pushState` /
// `replaceState` (monkey-patched, like every analytics SDK does it) plus the
// browser-driven `popstate` and `hashchange`.
//
// History is patched once per page and never restored: a project switch
// mounts the next tracker before the old one is disposed, and another
// library may wrap history after us. Putting the original back would drop
// both. Disposing only unsubscribes.

type HistoryWriter = History["pushState"];

const subscribers = new Set<() => void>();
let isPatched = false;

const patch = (method: "pushState" | "replaceState"): void => {
	// oxlint-disable-next-line typescript-eslint/unbound-method -- rebound with bind on the next line
	const bound: HistoryWriter = history[method].bind(history);
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Parameters<History["pushState"]> is a mutable vendor tuple
	history[method] = (...args: Parameters<HistoryWriter>): void => {
		bound(...args);
		for (const notify of subscribers) {
			notify();
		}
	};
};

// Query parameters whose names look like credentials: magic-link tokens,
// OAuth codes and state, signed-URL signatures, session ids. The page URL is
// stored with every piece of feedback, so they are removed before it leaves
// the page. Substring match – `access_token`, `X-Amz-Signature`, `apiKey`.
const SECRET_PARAM =
	/token|code|passw|pwd|secret|key|signature|auth|session|state|jwt/iu;

/** `href` without the query parameters whose names look secret. */
export const redactUrl = (href: string): string => {
	const url = new URL(href);
	const names = [...url.searchParams.keys()].filter((name) =>
		SECRET_PARAM.test(name),
	);
	for (const name of names) {
		url.searchParams.delete(name);
	}
	// Re-serializing rewrites the encoding of the other parameters, so a
	// URL with nothing to remove is returned as it was.
	return names.length === 0 ? href : url.href;
};

export const installPageTracker = (onChange: () => void): (() => void) => {
	// A subscriber that throws must not reach the host router: it would
	// surface from the router's own `pushState` call.
	const notify = (): void => {
		try {
			onChange();
		} catch (error) {
			console.error("[weloop]", error);
		}
	};
	if (!isPatched) {
		isPatched = true;
		patch("pushState");
		patch("replaceState");
	}
	subscribers.add(notify);
	window.addEventListener("popstate", notify);
	window.addEventListener("hashchange", notify);

	return () => {
		subscribers.delete(notify);
		window.removeEventListener("popstate", notify);
		window.removeEventListener("hashchange", notify);
	};
};
